import Debug from '../utils/Debug.js';

/**
 * SpriteAtlasBuilder - Packs multiple sprite frames into a single texture atlas
 *
 * This eliminates flickering by using background-position instead of changing
 * background-image when switching animation frames.
 *
 * The atlas is organized as a grid where each cell contains one frame.
 * All frames must have the same dimensions (spriteWidth x spriteHeight).
 */
export default class SpriteAtlasBuilder {
  /**
   * Build a sprite atlas from character metadata
   * @param {CharacterMetadata} metadata - Character metadata with frame info
   * @returns {Promise<SpriteAtlas>} The built atlas
   */
  static async build(metadata) {
    const frameWidth = metadata.spriteWidth;
    const frameHeight = metadata.spriteHeight;

    // Collect all unique frame paths
    const framePaths = this.collectFramePaths(metadata);
    const frameCount = framePaths.length;

    if (frameCount === 0) {
      throw new Error('No frames found in metadata');
    }

    // Calculate optimal atlas dimensions (prefer wider than tall)
    const { cols, rows } = this.calculateAtlasDimensions(frameCount);
    const atlasWidth = cols * frameWidth;
    const atlasHeight = rows * frameHeight;

    // Create canvas for the atlas
    const canvas = document.createElement('canvas');
    canvas.width = atlasWidth;
    canvas.height = atlasHeight;
    const ctx = canvas.getContext('2d');

    // Disable image smoothing for pixel art
    ctx.imageSmoothingEnabled = false;

    // Load all images and draw them onto the atlas
    const frameMap = new Map(); // path -> { x, y, col, row }
    const loadPromises = [];

    framePaths.forEach((path, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = col * frameWidth;
      const y = row * frameHeight;

      // Store mapping
      frameMap.set(path, { x, y, col, row, index });

      // Load and draw image
      const promise = this.loadImage(path).then(img => {
        ctx.drawImage(img, x, y, frameWidth, frameHeight);
      }).catch(err => {
        Debug.warn('AtlasBuilder', `Failed to load frame: ${path}`, err);
        // Draw a placeholder for missing frames
        ctx.fillStyle = '#ff00ff';
        ctx.fillRect(x, y, frameWidth, frameHeight);
      });

      loadPromises.push(promise);
    });

    // Wait for all images to load
    await Promise.all(loadPromises);

    // Convert canvas to blob URL for better performance than data URL
    const blobUrl = await this.canvasToBlobUrl(canvas);

    // Create and return the atlas object
    return new SpriteAtlas({
      url: blobUrl,
      width: atlasWidth,
      height: atlasHeight,
      frameWidth,
      frameHeight,
      cols,
      rows,
      frameMap,
      metadata
    });
  }

  /**
   * Collect all unique frame paths from metadata
   * @param {CharacterMetadata} metadata
   * @returns {string[]} Array of frame paths
   */
  static collectFramePaths(metadata) {
    const paths = new Set();

    // Add rotation frames
    for (const direction of Object.keys(metadata.rotations)) {
      const path = metadata.getRotationFrame(direction);
      if (path) paths.add(path);
    }

    // Add animation frames
    for (const animName of metadata.animationNames) {
      const directions = metadata.getAnimationDirections(animName);
      for (const direction of directions) {
        const frames = metadata.getAnimationFrames(animName, direction);
        if (frames) {
          frames.forEach(path => paths.add(path));
        }
      }
    }

    return Array.from(paths);
  }

  /**
   * Calculate optimal atlas dimensions
   * @param {number} frameCount - Total number of frames
   * @returns {{cols: number, rows: number}}
   */
  static calculateAtlasDimensions(frameCount) {
    // Aim for roughly square atlas, but prefer wider
    const sqrt = Math.sqrt(frameCount);
    const cols = Math.ceil(sqrt * 1.5); // Slightly wider
    const rows = Math.ceil(frameCount / cols);

    return { cols, rows };
  }

  /**
   * Load an image from a URL
   * @param {string} src - Image source URL
   * @returns {Promise<HTMLImageElement>}
   */
  static loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load: ${src}`));
      img.src = src;
    });
  }

  /**
   * Convert canvas to blob URL
   * @param {HTMLCanvasElement} canvas
   * @returns {Promise<string>}
   */
  static canvasToBlobUrl(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) {
          resolve(URL.createObjectURL(blob));
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      }, 'image/png');
    });
  }
}

/**
 * SpriteAtlas - Represents a built sprite atlas
 *
 * Provides methods to get frame positions within the atlas
 */
export class SpriteAtlas {
  constructor(options) {
    this.url = options.url;
    this.width = options.width;
    this.height = options.height;
    this.frameWidth = options.frameWidth;
    this.frameHeight = options.frameHeight;
    this.cols = options.cols;
    this.rows = options.rows;
    this.frameMap = options.frameMap;
    this.metadata = options.metadata;

    // Build optimized lookup tables
    this._buildLookupTables();
  }

  /**
   * Build lookup tables for fast frame access
   */
  _buildLookupTables() {
    // Create nested lookup: animation -> direction -> frameIndex -> {x, y}
    this.animationLookup = new Map();

    for (const animName of this.metadata.animationNames) {
      const directionMap = new Map();

      for (const direction of this.metadata.getAnimationDirections(animName)) {
        const frames = this.metadata.getAnimationFrames(animName, direction);
        const framePositions = [];

        if (frames) {
          for (const framePath of frames) {
            const atlasPos = this.frameMap.get(framePath);
            if (atlasPos) {
              framePositions.push({
                x: atlasPos.x,
                y: atlasPos.y,
                bgPosition: `-${atlasPos.x}px -${atlasPos.y}px`
              });
            }
          }
        }

        directionMap.set(direction, framePositions);
      }

      this.animationLookup.set(animName, directionMap);
    }

    // Build rotation lookup
    this.rotationLookup = new Map();
    for (const direction of Object.keys(this.metadata.rotations)) {
      const path = this.metadata.getRotationFrame(direction);
      if (path) {
        const atlasPos = this.frameMap.get(path);
        if (atlasPos) {
          this.rotationLookup.set(direction, {
            x: atlasPos.x,
            y: atlasPos.y,
            bgPosition: `-${atlasPos.x}px -${atlasPos.y}px`
          });
        }
      }
    }
  }

  /**
   * Get frame position for an animation frame
   * @param {string} animName - Animation name
   * @param {string} direction - Direction
   * @param {number} frameIndex - Frame index
   * @returns {{x: number, y: number, bgPosition: string}|null}
   */
  getAnimationFrame(animName, direction, frameIndex) {
    const directionMap = this.animationLookup.get(animName);
    if (!directionMap) return null;

    const frames = directionMap.get(direction);
    if (!frames || frameIndex < 0 || frameIndex >= frames.length) return null;

    return frames[frameIndex];
  }

  /**
   * Get frame count for an animation in a direction
   * @param {string} animName - Animation name
   * @param {string} direction - Direction
   * @returns {number}
   */
  getFrameCount(animName, direction) {
    const directionMap = this.animationLookup.get(animName);
    if (!directionMap) return 0;

    const frames = directionMap.get(direction);
    return frames ? frames.length : 0;
  }

  /**
   * Get frame position for a static rotation
   * @param {string} direction - Direction
   * @returns {{x: number, y: number, bgPosition: string}|null}
   */
  getRotationFrame(direction) {
    return this.rotationLookup.get(direction) || null;
  }

  /**
   * Get CSS properties for using this atlas
   * @returns {Object} CSS properties object
   */
  getCSSProperties() {
    return {
      backgroundImage: `url(${this.url})`,
      backgroundSize: `${this.width}px ${this.height}px`,
      width: `${this.frameWidth}px`,
      height: `${this.frameHeight}px`,
      imageRendering: 'pixelated'
    };
  }

  /**
   * Clean up resources (revoke blob URL)
   */
  destroy() {
    if (this.url.startsWith('blob:')) {
      URL.revokeObjectURL(this.url);
    }
    this.frameMap.clear();
    this.animationLookup.clear();
    this.rotationLookup.clear();
  }
}
